import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Mail, ShieldAlert, Building2 } from "lucide-react";
import { useT } from "../i18n";
import { PLANS, formatPrice, savingVs, href, CONTACTS, COMPANY, STORES, SCREENS } from "../content/shared";
import { PageHero, Reveal, SectionHead, FaqList, ArrowLink, Screen, StoreBadges, QR, Chat } from "../components/kit";
import { DownloadBand } from "./Home";
import { CheckList } from "../components/Demo";
import D from "../content/demos";

export function Pricing() {
  const { lang, t } = useT();
  const p = t.pricing;
  return (
    <>
      <PageHero eyebrow={p.eyebrow} lines={p.h1} lead={p.lead} testid="pricing-hero" />
      <section className="container-x pb-16" data-testid="pricing-plans">
        <div className="grid gap-4 md:grid-cols-3">
          {PLANS.map((pl, i) => {
            const best = pl.id === "yearly";
            return (
              <Reveal key={pl.id} delay={i * 0.06} className={`card relative flex flex-col p-8 ${best ? "border-primary ring-1 ring-primary" : ""}`}>
                <div data-testid={`pricing-plan-${pl.id}`}>
                  <p className="font-display text-xl font-semibold">{p.names[pl.id]}</p>
                  <p className="mt-6 font-display text-5xl font-semibold tracking-tight">{formatPrice(pl.price, lang)}</p>
                  <p className="mt-1 text-muted-foreground">{p.per[pl.id]}</p>
                  <p className="mt-6 text-sm text-muted-foreground">{p.equiv.replace("{x}", formatPrice(pl.price / pl.weeks, lang))}</p>
                  {pl.id === "monthly" && <p className="mt-2 text-sm font-medium text-primary">{p.saveMonthly.replace("{n}", savingVs(pl, "weekly"))}</p>}
                  {best && <p className="mt-2 text-sm font-medium text-primary">{p.saveYearly.replace("{n}", savingVs(pl, "monthly"))}</p>}
                  <p className="mt-6 border-t border-border pt-6 text-sm">{p.planNote[pl.id]}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
        <p className="mt-6 max-w-3xl text-sm text-muted-foreground">{p.priceNote}</p>
        <div className="mt-8 rounded-2xl border border-border bg-surface-2 p-6 md:p-8">
          <h2 className="text-xl md:text-2xl">{p.includedH}</h2>
          <p className="mt-3 max-w-3xl text-muted-foreground">{p.included}</p>
          <p className="mt-3 max-w-3xl text-muted-foreground" data-testid="pricing-calls-note">{D[lang].pricingNote}</p>
        </div>
      </section>
      <section className="border-y border-border bg-surface" data-testid="pricing-billing">
        <div className="container-x section grid gap-12 lg:grid-cols-[1fr_1.6fr]">
          <SectionHead eyebrow={p.billingEyebrow} title={p.billingH2} />
          <dl className="divide-y divide-border border-y border-border">
            {p.billing.map((b, i) => (
              <div key={i} className="grid gap-2 py-6 sm:grid-cols-[12rem_1fr] sm:gap-8">
                <dt className="font-display font-semibold">{b.t}</dt>
                <dd className="text-muted-foreground">{b.d}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>
      <section className="container-x section grid gap-12 lg:grid-cols-[1fr_1.6fr]">
        <SectionHead title={p.faqH2} />
        <FaqList items={p.faq} prefix="pricing-faq" />
      </section>
      <DownloadBand />
    </>
  );
}

export function Safety() {
  const { lang, t } = useT();
  const s = t.safety;
  return (
    <>
      <PageHero eyebrow={s.eyebrow} lines={s.h1} lead={s.lead} testid="safety-hero" />
      <section className="border-t border-border bg-surface">
        <div className="container-x section grid items-center gap-12 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <SectionHead title={s.seeH2} lead={s.seeLead} />
            <div className="mt-10 grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2">
              {[[s.seeYes, true], [s.seeNo, false]].map(([list, ok], k) => (
                <div key={k} className="bg-surface p-6">
                  <p className={`text-sm font-semibold ${ok ? "text-success" : "text-danger"}`}>{ok ? s.shownLabel : s.notShownLabel}</p>
                  {ok ? <CheckList items={list} className="mt-4 text-sm" /> : <ul className="mt-4 space-y-2.5">{list.map((x, i) => <li key={i} className="text-sm">{x}</li>)}</ul>}
                </div>
              ))}
            </div>
          </div>
          <Reveal className="mx-auto w-full max-w-[280px]"><Screen src={SCREENS.chat} alt={s.screenAlt} /></Reveal>
        </div>
      </section>
      <section className="container-x section">
        <SectionHead eyebrow={s.controlEyebrow} title={s.controlH2} />
        <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-border bg-border md:grid-cols-2">
          {[...s.control, D[lang].safety].map((c, i) => (
            <Reveal key={i} className="bg-surface p-7 md:p-9"><h3 className="text-xl">{c.t}</h3><p className="mt-3 text-muted-foreground">{c.d}</p></Reveal>
          ))}
        </div>
      </section>
      <section className="bg-[#171940] text-white" data-testid="safety-not-emergency">
        <div className="container-x section grid gap-12 lg:grid-cols-2">
          <Reveal>
            <ShieldAlert className="h-8 w-8 text-[#F5C842]" aria-hidden />
            <h2 className="mt-6 text-white">{s.emergencyH2}</h2>
            <p className="mt-5 max-w-lg text-white/80">{s.emergencyText}</p>
          </Reveal>
          <Reveal delay={0.08}>
            <h3 className="text-xl text-white">{s.notH}</h3>
            <ul className="mt-5 space-y-3">{s.not.map((n, i) => <li key={i} className="flex gap-3 text-white/85"><span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#F5C842]" />{n}</li>)}</ul>
          </Reveal>
        </div>
      </section>
      <section className="container-x section grid gap-10 md:grid-cols-2">
        <Reveal><h2 className="text-2xl md:text-3xl">{s.docsH}</h2><p className="mt-4 max-w-lg text-muted-foreground">{s.docsText}</p></Reveal>
        <div className="flex flex-col gap-3">
          {[["privacy", t.footer.privacy], ["child", t.footer.child], ["terms", t.footer.terms], ["support", s.reportLink]].map(([k, l]) => (
            <ArrowLink key={k} to={href(lang, k)} testid={`safety-doc-${k}`} className="text-base">{l}</ArrowLink>
          ))}
        </div>
      </section>
    </>
  );
}

export function Help() {
  const { lang, t } = useT();
  const h = { ...t.help, cats: [...t.help.cats.slice(0, 3), D[lang].help, ...t.help.cats.slice(3)] };
  const [q, setQ] = useState("");
  const cats = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return h.cats;
    return h.cats.map((c) => ({ ...c, items: c.items.filter(([a, b]) => `${a} ${b}`.toLowerCase().includes(s)) })).filter((c) => c.items.length);
  }, [q, lang]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <PageHero eyebrow={h.eyebrow} lines={h.h1} lead={h.lead} testid="help-hero">
        <label className="relative mt-10 block max-w-xl">
          <span className="sr-only">{h.searchPh}</span>
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={h.searchPh} data-testid="help-search-input" className="h-14 w-full rounded-full border border-input bg-surface pl-12 pr-5 text-base outline-none focus:border-primary" />
        </label>
      </PageHero>
      <section className="container-x grid gap-12 pb-20 lg:grid-cols-[16rem_1fr]">
        <nav aria-label={h.catsLabel} className="lg:sticky lg:top-28 lg:self-start">
          <ul className="flex flex-wrap gap-2 lg:flex-col lg:gap-1">
            {h.cats.map((c) => (
              <li key={c.id}><a href={`#${c.id}`} className="block rounded-full border border-border px-3 py-1.5 text-sm hover:text-primary lg:rounded-lg lg:border-0 lg:px-2" data-testid={`help-cat-link-${c.id}`}>{c.title}</a></li>
            ))}
          </ul>
        </nav>
        <div className="space-y-16">
          {cats.length === 0 && <p className="text-muted-foreground" data-testid="help-no-results">{h.noResults}</p>}
          {cats.map((c) => (
            <section key={c.id} id={c.id} className="scroll-mt-28" data-testid={`help-cat-${c.id}`}>
              <h2 className="mb-6 text-2xl md:text-3xl">{c.title}</h2>
              <FaqList items={c.items} prefix={`help-${c.id}`} />
            </section>
          ))}
          <div className="rounded-2xl border border-border bg-surface p-8">
            <h2 className="text-2xl">{h.stillH}</h2>
            <p className="mt-2 text-muted-foreground">{h.stillD}</p>
            <Link to={href(lang, "support")} className="btn-primary mt-6" data-testid="help-contact-btn">{t.nav.support}</Link>
          </div>
        </div>
      </section>
    </>
  );
}

export function About() {
  const { lang, t } = useT();
  const a = t.about;
  return (
    <>
      <PageHero eyebrow={a.eyebrow} lines={a.h1} lead={a.lead} testid="about-hero" />
      <section className="container-x">
        <Reveal className="overflow-hidden rounded-[2rem]"><img src="/images/uc-parking.webp" alt={a.photoAlt} className="aspect-[4/3] w-full object-cover md:aspect-[21/9]" /></Reveal>
      </section>
      <section className="container-x section grid gap-12 lg:grid-cols-3">
        {a.blocks.map((b, i) => (
          <Reveal key={i} delay={i * 0.06} className="border-t border-foreground pt-6">
            <h2 className="text-2xl">{b.t}</h2>
            <p className="mt-4 text-muted-foreground">{b.d}</p>
          </Reveal>
        ))}
      </section>
      <section className="border-t border-border bg-surface">
        <div className="container-x section grid gap-10 md:grid-cols-2">
          <SectionHead title={a.companyH} lead={a.companyText} />
          <address className="not-italic">
            <p className="font-display text-lg font-semibold">{COMPANY.name}</p>
            <p className="mt-2 text-muted-foreground">{COMPANY.street}<br />{COMPANY.city}<br />{COMPANY.country}</p>
            <p className="mt-4"><a className="link" href={`mailto:${CONTACTS.info}`}>{CONTACTS.info}</a></p>
            <ArrowLink to={href(lang, "impressum")} className="mt-6" testid="about-impressum-link">{t.footer.impressum}</ArrowLink>
          </address>
        </div>
      </section>
      <DownloadBand />
    </>
  );
}

export function Download() {
  const { t } = useT();
  const d = t.download;
  const shots = [["register", d.shots[0]], ["search", d.shots[1]], ["chat", d.shots[2]]];
  return (
    <>
      <PageHero eyebrow={d.eyebrow} lines={d.h1} lead={d.lead} testid="download-hero">
        <StoreBadges className="mt-8" prefix="download" />
      </PageHero>
      <section className="container-x pb-16">
        <div className="grid gap-px overflow-hidden rounded-2xl border border-border bg-border md:grid-cols-2">
          {[["ios", d.iosH, d.iosText, STORES.ios, "App Store"], ["android", d.androidH, d.androidText, STORES.android, "Google Play"]].map(([id, h, txt, url, label]) => (
            <div key={id} className="flex flex-col justify-between gap-8 bg-surface p-8 sm:flex-row sm:items-center" data-testid={`download-${id}`}>
              <div>
                <h2 className="text-2xl">{h}</h2>
                <p className="mt-2 max-w-sm text-muted-foreground">{txt}</p>
                <a href={url} target="_blank" rel="noopener noreferrer" className="link mt-4 inline-block text-sm" data-testid={`download-${id}-link`}>{d.open.replace("{store}", label)}</a>
              </div>
              <QR url={url} label={d.scan.replace("{store}", label)} testid={`download-qr-${id}`} />
            </div>
          ))}
        </div>
      </section>
      <section className="border-y border-border bg-surface-2">
        <div className="container-x section">
          <SectionHead title={d.shotsH} lead={d.shotsLead} />
          <div className="mt-12 grid gap-8 sm:grid-cols-3">
            {shots.map(([k, cap]) => (
              <Reveal key={k} as="figure" className="mx-auto w-full max-w-[280px]">
                <Screen src={SCREENS[k]} alt={cap} />
                <figcaption className="mt-4 text-sm text-muted-foreground">{cap}</figcaption>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <section className="container-x section grid gap-12 lg:grid-cols-2">
        <SectionHead title={d.afterH} />
        <ol className="space-y-5">
          {d.after.map((a, i) => <li key={i} className="grid grid-cols-[2.5rem_1fr] gap-4 border-b border-border pb-5"><span className="font-mono text-sm text-primary">0{i + 1}</span><span>{a}</span></li>)}
        </ol>
      </section>
    </>
  );
}

export function NotFound() {
  const { lang, t } = useT();
  return (
    <section className="container-x section" data-testid="not-found">
      <p className="font-mono text-sm text-primary">404</p>
      <h1 className="mt-4">{t.notFound.h1}</h1>
      <p className="lead mt-6">{t.notFound.text}</p>
      <Link to={href(lang, "home")} className="btn-primary mt-8" data-testid="not-found-home-btn">{t.notFound.home}</Link>
    </section>
  );
}

export const ContactAside = () => {
  const { lang, t } = useT();
  const c = t.contact;
  return (
    <aside className="space-y-8">
      {[[Mail, c.channels.support, CONTACTS.support], [Mail, c.channels.info, CONTACTS.info], [ShieldAlert, c.channels.safety, CONTACTS.safety]].map(([Icon, label, mail]) => (
        <div key={mail} className="flex gap-4 border-b border-border pb-6">
          <Icon className="mt-1 h-5 w-5 text-primary" aria-hidden />
          <div><p className="text-sm text-muted-foreground">{label}</p><a href={`mailto:${mail}`} className="link font-medium" data-testid={`contact-mail-${mail.split("@")[0]}`}>{mail}</a></div>
        </div>
      ))}
      <div className="rounded-2xl border border-signal/60 bg-signal/10 p-6"><p className="font-semibold">{c.abuseH}</p><p className="mt-2 text-sm">{c.abuseText}</p></div>
      <div className="flex gap-4">
        <Building2 className="mt-1 h-5 w-5 text-primary" aria-hidden />
        <address className="not-italic text-sm text-muted-foreground"><span className="font-medium text-foreground">{COMPANY.name}</span><br />{COMPANY.street}<br />{COMPANY.city}, {COMPANY.country}</address>
      </div>
      <ArrowLink to={href(lang, "help")} testid="contact-help-link">{c.helpLink}</ArrowLink>
    </aside>
  );
};
